import {requestDisableVideo, requestVideoStream} from '../video/camera';
import AudioRecorder from '../audio/audio-recorder';
import SharedAudioContext from '../audio/shared-audio-context';
import {
    classifyAudioFeatures,
    createAudioClassifier,
    samplesToAudioFeatures
} from '../audio/audio-training-features';
import loadVisionLibraries from './load-vision-libraries';

const TRAINING_INPUT_TYPES = {
    IMAGE: 'image',
    AUDIO: 'audio'
};

class VisionClassifierSession {
    constructor (runtime) {
        this.runtime = runtime;
        this.labels = [];
        this.labelTypes = {};
        this.activeInputType = TRAINING_INPUT_TYPES.IMAGE;
        this.trainingCounts = {};
        this.examplesByLabel = {};
        this.resetPredictions();
        this.imagePredictionLabel = '';
        this.imagePredictionConfidence = 0;
        this.imagePredictionConfidences = {};
        this.audioPredictionLabel = '';
        this.audioPredictionConfidence = 0;
        this.audioPredictionConfidences = {};
        this.isPredicting = false;
        this.isInitializing = false;
        this.modelReady = false;
        this.status = 'Opret en eller flere klasser, og tilføj derefter eksempler til hver af dem.';
        this.error = '';
        this.previewVisible = false;
        this.listeners = new Set();
        this.stream = null;
        this.analysisVideo = document.createElement('video');
        this.analysisVideo.setAttribute('playsinline', true);
        this.analysisVideo.muted = true;
        this.analysisVideo.autoplay = true;
        this.classifier = null;
        this.audioClassifier = createAudioClassifier();
        this.audioRecorder = null;
        this.audioContext = null;
        this.audioStream = null;
        this.audioSourceNode = null;
        this.audioProcessorNode = null;
        this.audioPredictionBuffer = [];
        this.audioLevel = 0;
        this.isRecordingAudio = false;
        this.mobilenetModel = null;
        this.tf = null;
        this._predictFrame = null;
        this._captureInterval = null;
        this._captureLabel = null;
        this._handleClearRequest = this.clearTraining.bind(this);
        this._handleBeforeUnload = this.teardown.bind(this);

        this.runtime.on('VISION_TRAINER_CLEAR_REQUEST', this._handleClearRequest);
        window.addEventListener('beforeunload', this._handleBeforeUnload);
        this._syncRuntimeBridge();
    }

    async dataUrlToImageData (dataUrl) {
        const image = new Image();
        image.decoding = 'async';

        await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = () => reject(new Error('Et traeningsbillede kunne ikke indlaeses.'));
            image.src = dataUrl;
        });

        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || image.width || 224;
        canvas.height = image.naturalHeight || image.height || 224;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        return context.getImageData(0, 0, canvas.width, canvas.height);
    }

    fileToDataUrl (file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Billedfilen kunne ikke indlaeses.'));
            reader.readAsDataURL(file);
        });
    }

    async fileToAudioExample (file) {
        const arrayBuffer = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Lydfilen kunne ikke indlaeses.'));
            reader.readAsArrayBuffer(file);
        });
        const audioContext = new SharedAudioContext();
        const audioBuffer = await new Promise((resolve, reject) => {
            audioContext.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
        });
        const samples = new Float32Array(audioBuffer.getChannelData(0));
        return this.createAudioExample(samples, audioBuffer.sampleRate, file.name);
    }

    subscribe (listener) {
        this.listeners.add(listener);
        listener(this.getState());
        return () => {
            this.listeners.delete(listener);
        };
    }

    getState () {
        return {
            labels: this.labels.slice(),
            labelTypes: Object.assign({}, this.labelTypes),
            activeInputType: this.activeInputType,
            trainingCounts: Object.assign({}, this.trainingCounts),
            examplesByLabel: this.getSerializableExamples(),
            currentLabel: this.currentLabel,
            currentConfidence: this.currentConfidence,
            confidences: Object.assign({}, this.confidences),
            imagePredictionLabel: this.imagePredictionLabel,
            imagePredictionConfidence: this.imagePredictionConfidence,
            imagePredictionConfidences: Object.assign({}, this.imagePredictionConfidences),
            audioPredictionLabel: this.audioPredictionLabel,
            audioPredictionConfidence: this.audioPredictionConfidence,
            audioPredictionConfidences: Object.assign({}, this.audioPredictionConfidences),
            isPredicting: this.isPredicting,
            isInitializing: this.isInitializing,
            modelReady: this.modelReady,
            status: this.status,
            error: this.error,
            hasExamples: this.getExampleTotal() > 0,
            hasLabels: this.labels.length > 0,
            streamReady: Boolean(this.stream),
            audioLevel: this.audioLevel,
            isRecordingAudio: this.isRecordingAudio
        };
    }

    getSerializableExamples () {
        return this.labels.reduce((result, label) => {
            result[label] = (this.examplesByLabel[label] || []).map(example => ({
                id: example.id,
                previewUrl: example.previewUrl,
                duration: example.duration,
                waveform: example.waveform,
                type: example.type || TRAINING_INPUT_TYPES.IMAGE
            }));
            return result;
        }, {});
    }

    exportTrainingData () {
        return {
            labels: this.labels.slice(),
            labelTypes: Object.assign({}, this.labelTypes),
            examplesByLabel: this.labels.reduce((result, label) => {
                result[label] = (this.examplesByLabel[label] || []).map(example => ({
                    id: example.id,
                    previewUrl: example.previewUrl,
                    type: example.type || TRAINING_INPUT_TYPES.IMAGE,
                    samples: example.samples ? Array.from(example.samples) : null,
                    sampleRate: example.sampleRate,
                    duration: example.duration,
                    waveform: example.waveform
                }));
                return result;
            }, {})
        };
    }

    async importTrainingData (trainingData) {
        await this.initialize();
        if (!this.modelReady) {
            return;
        }

        this.stopAddingExamples();
        this.stopPrediction();
        if (this.classifier) {
            this.classifier.clearAllClasses();
        }
        this.audioClassifier.clearAllClasses();

        this.labels = Array.isArray(trainingData && trainingData.labels) ?
            trainingData.labels.map(label => this.normalizeLabel(label)).filter(Boolean) :
            [];
        this.labelTypes = this.labels.reduce((result, label) => {
            const storedType = trainingData && trainingData.labelTypes && trainingData.labelTypes[label];
            result[label] = storedType === TRAINING_INPUT_TYPES.AUDIO ?
                TRAINING_INPUT_TYPES.AUDIO :
                TRAINING_INPUT_TYPES.IMAGE;
            return result;
        }, {});
        this.trainingCounts = {};
        this.examplesByLabel = {};
        this.resetPredictions();
        this.error = '';

        await Promise.all(this.labels.map(async label => {
            this.examplesByLabel[label] = [];

            const examples =
                (trainingData && trainingData.examplesByLabel && trainingData.examplesByLabel[label]) || [];
            const restoredExamples = await Promise.all(examples.map(async storedExample => {
                if (!storedExample) {
                    return null;
                }

                if (this.getLabelType(label) === TRAINING_INPUT_TYPES.AUDIO) {
                    if (!storedExample.samples || !storedExample.sampleRate) return null;
                    return this.createAudioExample(
                        new Float32Array(storedExample.samples),
                        storedExample.sampleRate,
                        storedExample.previewUrl || 'lydeksempel',
                        storedExample.id
                    );
                }

                if (!storedExample.previewUrl) return null;

                const imageData = await this.dataUrlToImageData(storedExample.previewUrl);
                return this.createImageExample(imageData, storedExample.previewUrl, storedExample.id);
            }));

            restoredExamples.filter(Boolean).forEach(example => {
                this.addStoredExample(label, example);
            });
            this.trainingCounts[label] = this.examplesByLabel[label].length;
        }));

        this.status = this.getExampleTotal() > 0 ?
            'Model og traeningsdata er indlaest fra filen.' :
            'Projektet blev indlaest uden gemt modeldata.';
        this.emitChange();
    }

    emitChange () {
        const nextState = this.getState();
        this.listeners.forEach(listener => listener(nextState));
        this._syncRuntimeBridge();
    }

    _setErrorState (error, fallbackStatus) {
        this.error = error.message || String(error);
        this.status = fallbackStatus;
        this.emitChange();
    }

    _syncRuntimeBridge () {
        if (!this.runtime.visionClassifier) {
            return;
        }

        this.runtime.visionClassifier.setModelReady(this.modelReady);
        this.runtime.visionClassifier.setTrainingData({
            labels: this.labels,
            trainingCounts: this.trainingCounts
        });
        this.runtime.visionClassifier.setPredicting(this.isPredicting);
        this.runtime.visionClassifier.setPrediction({
            label: this.currentLabel,
            confidences: this.confidences
        });
    }

    async initialize () {
        if (this.modelReady || this.isInitializing) {
            return;
        }

        this.isInitializing = true;
        this.error = '';
        this.status = 'Indlæser TensorFlow og MobileNet...';
        this.emitChange();

        try {
            const {tf, mobilenet, knnClassifier} = await loadVisionLibraries();
            this.tf = tf;
            this.mobilenetModel = await mobilenet.load();
            this.classifier = knnClassifier.create();
            this.modelReady = true;
            this.status = 'Kamera og model er klar. Opret klasser og tilføj eksempler for at træne Databyggeren.';
        } catch (error) {
            this.error = error.message;
            this.status = 'Databibliotekerne kunne ikke indlæses.';
        } finally {
            this.isInitializing = false;
            this.emitChange();
        }
    }

    async ensureVideo () {
        if (this.stream) {
            return;
        }

        try {
            const stream = await requestVideoStream({
                width: {min: 480, ideal: 640},
                height: {min: 360, ideal: 480},
                facingMode: 'user'
            });

            this.stream = stream;
            this.analysisVideo.srcObject = stream;
            await new Promise(resolve => {
                this.analysisVideo.onloadedmetadata = () => resolve();
            });
            await this.analysisVideo.play();
            this.status = this.status || 'Kameraet er klar.';
            this.emitChange();
        } catch (error) {
            this._setErrorState(error, 'Kameraet kunne ikke startes.');
            throw error;
        }
    }

    async startPreview () {
        try {
            await this.initialize();
            if (!this.modelReady) return;
            await this.ensureVideo();
            this.previewVisible = true;
            this.emitChange();
        } catch (error) {
            this._setErrorState(error, 'Forhåndsvisningen kunne ikke startes.');
        }
    }

    async addExample (label) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!normalizedLabel) return;
        if (!this.labels.includes(normalizedLabel)) return;
        if (this.getLabelType(normalizedLabel) === TRAINING_INPUT_TYPES.AUDIO) return;

        await this.initialize();
        if (!this.modelReady) return;
        await this.ensureVideo();

        const example = this.captureExampleFrame();
        this.addStoredExample(normalizedLabel, example);

        this.trainingCounts[normalizedLabel] = (this.trainingCounts[normalizedLabel] || 0) + 1;
        this.status = `Tilføjede ét eksempel til "${normalizedLabel}".`;
        this.emitChange();
    }

    async addUploadedExamples (label, files) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!normalizedLabel) return;
        if (!this.labels.includes(normalizedLabel)) return;
        if (this.getLabelType(normalizedLabel) === TRAINING_INPUT_TYPES.AUDIO) {
            await this.addUploadedAudioExamples(normalizedLabel, files);
            return;
        }

        const imageFiles = Array.from(files || [])
            .filter(file => file && file.type && file.type.indexOf('image/') === 0);
        if (!imageFiles.length) {
            this.status = 'Vaelg en eller flere billedfiler.';
            this.emitChange();
            return;
        }

        try {
            await this.initialize();
            if (!this.modelReady) return;
            this.stopAddingExamples();

            const uploadedExamples = await Promise.all(imageFiles.map(async file => {
                const previewUrl = await this.fileToDataUrl(file);
                const imageData = await this.dataUrlToImageData(previewUrl);
                return this.createImageExample(imageData, previewUrl);
            }));

            uploadedExamples.forEach(example => {
                this.addStoredExample(normalizedLabel, example);
            });

            this.trainingCounts[normalizedLabel] =
                (this.trainingCounts[normalizedLabel] || 0) + uploadedExamples.length;
            this.status = `Uploadede ${uploadedExamples.length} billede${
                uploadedExamples.length === 1 ? '' : 'r'
            } til "${normalizedLabel}".`;
            this.emitChange();
        } catch (error) {
            this._setErrorState(error, 'Billedet kunne ikke uploades til datatraeningen.');
        }
    }

    async addUploadedAudioExamples (label, files) {
        const audioFiles = Array.from(files || [])
            .filter(file => file && file.type && file.type.indexOf('audio/') === 0);
        if (!audioFiles.length) {
            this.status = 'Vaelg en eller flere lydfiler.';
            this.emitChange();
            return;
        }

        try {
            this.stopAddingExamples();
            const uploadedExamples = await Promise.all(audioFiles.map(file => this.fileToAudioExample(file)));
            uploadedExamples.forEach(example => {
                this.addStoredExample(label, example);
            });
            this.trainingCounts[label] = (this.trainingCounts[label] || 0) + uploadedExamples.length;
            this.status = `Uploadede ${uploadedExamples.length} lydklip til "${label}".`;
            this.emitChange();
        } catch (error) {
            this._setErrorState(error, 'Lydfilen kunne ikke uploades til datatraeningen.');
        }
    }

    async startAddingExamples (label) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!normalizedLabel) return;
        if (this.getLabelType(normalizedLabel) === TRAINING_INPUT_TYPES.AUDIO) {
            return;
        }
        if (this._captureInterval && this._captureLabel === normalizedLabel) {
            return;
        }

        this.stopAddingExamples();
        this._captureLabel = normalizedLabel;

        await this.addExample(normalizedLabel);

        this._captureInterval = setInterval(() => {
            this.addExample(normalizedLabel);
        }, 220);
    }

    stopAddingExamples () {
        if (this.audioRecorder) {
            this.stopRecordingAudioExample();
            return;
        }
        if (this._captureInterval) {
            clearInterval(this._captureInterval);
            this._captureInterval = null;
        }
        this._captureLabel = null;
    }

    captureExampleFrame () {
        const canvas = document.createElement('canvas');
        canvas.width = this.analysisVideo.videoWidth || 224;
        canvas.height = this.analysisVideo.videoHeight || 224;
        const context = canvas.getContext('2d');
        context.drawImage(this.analysisVideo, 0, 0, canvas.width, canvas.height);

        return this.createImageExample(
            context.getImageData(0, 0, canvas.width, canvas.height),
            canvas.toDataURL('image/jpeg', 0.85)
        );
    }

    createExampleId () {
        return `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;
    }

    createImageExample (imageData, previewUrl, id) {
        return {
            id: id || this.createExampleId(),
            imageData,
            previewUrl,
            type: TRAINING_INPUT_TYPES.IMAGE
        };
    }

    createAudioWaveform (samples, pointCount = 36) {
        if (!samples || !samples.length) {
            return [];
        }

        const bucketSize = Math.max(1, Math.floor(samples.length / pointCount));
        const waveform = [];
        for (let bucketStart = 0; bucketStart < samples.length && waveform.length < pointCount; bucketStart += bucketSize) {
            const bucketEnd = Math.min(samples.length, bucketStart + bucketSize);
            let peak = 0;
            for (let i = bucketStart; i < bucketEnd; i++) {
                peak = Math.max(peak, Math.abs(samples[i]));
            }
            waveform.push(Math.min(1, peak));
        }
        return waveform;
    }

    createAudioExample (samples, sampleRate, name, id) {
        const duration = sampleRate ? samples.length / sampleRate : 0;
        return {
            id: id || this.createExampleId(),
            samples,
            sampleRate,
            duration,
            waveform: this.createAudioWaveform(samples),
            previewUrl: name || `${duration.toFixed(1)} sek. lyd`,
            type: TRAINING_INPUT_TYPES.AUDIO,
            features: samplesToAudioFeatures(samples)
        };
    }

    addRecordedAudioExample (label, samples, sampleRate) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!normalizedLabel || !this.labels.includes(normalizedLabel)) return;
        if (this.getLabelType(normalizedLabel) !== TRAINING_INPUT_TYPES.AUDIO) return;
        if (!samples || !samples.length || !sampleRate) {
            this.status = 'Optagelsen var tom. Proev igen.';
            this.emitChange();
            return;
        }

        const example = this.createAudioExample(
            samples,
            sampleRate,
            `${(samples.length / sampleRate).toFixed(1)} sek.`
        );
        this.addStoredExample(normalizedLabel, example);
        this.trainingCounts[normalizedLabel] = (this.trainingCounts[normalizedLabel] || 0) + 1;
        this.status = `Tilfoejede et lydeksempel til "${normalizedLabel}".`;
        this.emitChange();
    }

    addStoredExample (label, example) {
        if (!this.examplesByLabel[label]) {
            this.examplesByLabel[label] = [];
        }
        this.examplesByLabel[label].unshift(example);

        if (example.type === TRAINING_INPUT_TYPES.AUDIO) {
            this.audioClassifier.addExample(label, example.features);
            return;
        }

        const activation = this.tf.tidy(() =>
            this.mobilenetModel.infer(this.tf.browser.fromPixels(example.imageData), true)
        );
        this.classifier.addExample(activation, label);
        activation.dispose();
    }

    normalizeLabel (label) {
        return String(label || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
    }

    getLabelType (label) {
        return this.labelTypes[label] || TRAINING_INPUT_TYPES.IMAGE;
    }

    setPredictionForType (inputType, result) {
        const label = result.label || '';
        const confidence = label ? (result.confidences[label] || 0) : 0;
        const confidences = Object.assign({}, result.confidences);

        if (inputType === TRAINING_INPUT_TYPES.AUDIO) {
            this.audioPredictionLabel = label;
            this.audioPredictionConfidence = confidence;
            this.audioPredictionConfidences = confidences;
        } else {
            this.imagePredictionLabel = label;
            this.imagePredictionConfidence = confidence;
            this.imagePredictionConfidences = confidences;
        }

        this.currentLabel = label;
        this.currentConfidence = confidence;
        this.confidences = confidences;
    }

    clearPredictionForType (inputType) {
        if (inputType === TRAINING_INPUT_TYPES.AUDIO) {
            this.audioPredictionLabel = '';
            this.audioPredictionConfidence = 0;
            this.audioPredictionConfidences = {};
            return;
        }

        this.imagePredictionLabel = '';
        this.imagePredictionConfidence = 0;
        this.imagePredictionConfidences = {};
    }

    resetPredictions () {
        this.currentLabel = '';
        this.currentConfidence = 0;
        this.confidences = {};
        this.clearPredictionForType(TRAINING_INPUT_TYPES.IMAGE);
        this.clearPredictionForType(TRAINING_INPUT_TYPES.AUDIO);
    }

    setActiveInputType (inputType) {
        if (inputType !== TRAINING_INPUT_TYPES.IMAGE && inputType !== TRAINING_INPUT_TYPES.AUDIO) {
            return;
        }
        if (this.activeInputType === inputType) {
            return;
        }
        this.stopPrediction();
        this.activeInputType = inputType;
        this.resetPredictions();
        this.status = inputType === TRAINING_INPUT_TYPES.AUDIO ?
            'Lydtraening er aktiv. Brug mikrofonen til at forudsige lydklasser.' :
            'Billedtraening er aktiv. Brug kameraet til at forudsige billedklasser.';
        this.emitChange();
    }

    addLabel (label, inputType = TRAINING_INPUT_TYPES.IMAGE) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!normalizedLabel) {
            this.status = 'Skriv et klassenavn før du tilføjer det.';
            this.emitChange();
            return false;
        }
        if (this.labels.includes(normalizedLabel)) {
            this.status = `"${normalizedLabel}" findes allerede.`;
            this.emitChange();
            return false;
        }

        this.labels = [normalizedLabel].concat(this.labels);
        this.labelTypes[normalizedLabel] =
            inputType === TRAINING_INPUT_TYPES.AUDIO ? TRAINING_INPUT_TYPES.AUDIO : TRAINING_INPUT_TYPES.IMAGE;
        this.activeInputType = this.labelTypes[normalizedLabel];
        this.trainingCounts[normalizedLabel] = 0;
        this.examplesByLabel[normalizedLabel] = [];
        this.status = `Oprettede klassen "${normalizedLabel}".`;
        this.emitChange();
        return true;
    }

    async removeLabel (label) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!normalizedLabel || !this.labels.includes(normalizedLabel)) {
            return;
        }

        const removedType = this.getLabelType(normalizedLabel);
        this.labels = this.labels.filter(existingLabel => existingLabel !== normalizedLabel);
        delete this.labelTypes[normalizedLabel];
        delete this.trainingCounts[normalizedLabel];
        delete this.examplesByLabel[normalizedLabel];

        if (this.currentLabel === normalizedLabel) {
            this.currentLabel = '';
            this.currentConfidence = 0;
        }
        if (
            (removedType === TRAINING_INPUT_TYPES.AUDIO && this.audioPredictionLabel === normalizedLabel) ||
            (removedType === TRAINING_INPUT_TYPES.IMAGE && this.imagePredictionLabel === normalizedLabel)
        ) {
            this.clearPredictionForType(removedType);
        }

        this.confidences = Object.keys(this.confidences).reduce((result, key) => {
            if (this.normalizeLabel(key) !== normalizedLabel) {
                result[key] = this.confidences[key];
            }
            return result;
        }, {});

        if (!this.labels.length) {
            this.stopPrediction();
        }

        await this.rebuildClassifierFromStoredExamples();
        this.status = `Fjernede klassen "${normalizedLabel}".`;
        this.emitChange();
    }

    async removeExample (label, exampleId) {
        const normalizedLabel = this.normalizeLabel(label);
        if (!this.examplesByLabel[normalizedLabel]) {
            return;
        }

        this.examplesByLabel[normalizedLabel] = this.examplesByLabel[normalizedLabel]
            .filter(example => example.id !== exampleId);
        this.trainingCounts[normalizedLabel] = this.examplesByLabel[normalizedLabel].length;

        if (this.currentLabel === normalizedLabel && this.trainingCounts[normalizedLabel] === 0) {
            this.currentLabel = '';
            this.currentConfidence = 0;
        }
        if (this.trainingCounts[normalizedLabel] === 0) {
            this.clearPredictionForType(this.getLabelType(normalizedLabel));
        }

        if (this.getExampleTotal() === 0) {
            this.stopPrediction();
        }

        await this.rebuildClassifierFromStoredExamples();
        this.status = `Fjernede ét eksempel fra "${normalizedLabel}".`;
        this.emitChange();
    }

    async rebuildClassifierFromStoredExamples () {
        if (!this.modelReady) {
            return;
        }

        const {knnClassifier} = await loadVisionLibraries();
        this.classifier = knnClassifier.create();
        this.audioClassifier = createAudioClassifier();

        Object.keys(this.examplesByLabel).forEach(label => {
            (this.examplesByLabel[label] || []).forEach(example => {
                if (example.type === TRAINING_INPUT_TYPES.AUDIO) {
                    this.audioClassifier.addExample(label, example.features);
                    return;
                }
                const activation = this.tf.tidy(() =>
                    this.mobilenetModel.infer(this.tf.browser.fromPixels(example.imageData), true)
                );
                this.classifier.addExample(activation, label);
                activation.dispose();
            });
        });

        if (this.getExampleTotal() === 0) {
            this.resetPredictions();
        }
    }

    getExampleTotal () {
        return Object.keys(this.trainingCounts).reduce((sum, label) => sum + this.trainingCounts[label], 0);
    }

    getActiveExampleTotal () {
        return this.labels.reduce((sum, label) => (
            this.getLabelType(label) === this.activeInputType ? sum + (this.trainingCounts[label] || 0) : sum
        ), 0);
    }

    getExampleTotalForType (inputType) {
        return this.labels.reduce((sum, label) => (
            this.getLabelType(label) === inputType ? sum + (this.trainingCounts[label] || 0) : sum
        ), 0);
    }

    async startPrediction () {
        if (this.isPredicting || this.getExampleTotal() === 0 || !this.labels.length) {
            return;
        }

        const hasImageExamples = this.getExampleTotalForType(TRAINING_INPUT_TYPES.IMAGE) > 0;
        const hasAudioExamples = this.getExampleTotalForType(TRAINING_INPUT_TYPES.AUDIO) > 0;

        await this.initialize();
        if (!this.modelReady) return;

        try {
            if (hasImageExamples) {
                await this.ensureVideo();
            }
            if (hasAudioExamples) {
                await this.ensureAudioPredictionInput();
            }
        } catch (error) {
            this._setErrorState(error, 'Forudsigelsen kunne ikke startes.');
            return;
        }

        this.isPredicting = true;
        this.status = 'Dataforudsigelse kører nu. Billede og lyd kan reagere samtidig.';
        this.emitChange();
        if (hasImageExamples) {
            this._schedulePrediction();
        }
    }

    async startAudioPrediction () {
        try {
            await this.ensureAudioPredictionInput();
            this.isPredicting = true;
            this.status = 'Lydforudsigelse kører nu. Databyggeren-blokkene kan nu reagere.';
            this.emitChange();
        } catch (error) {
            this._setErrorState(error, 'Mikrofonen kunne ikke startes.');
        }
    }

    stopPrediction () {
        this.isPredicting = false;
        this.status = 'Dataforudsigelse er sat på pause.';
        if (this._predictFrame) {
            cancelAnimationFrame(this._predictFrame);
            this._predictFrame = null;
        }
        this.stopAudioPredictionInput();
        this.stopAudioPredictionInput();
        this.emitChange();
        if (!this.previewVisible) {
            this.stopVideo();
        }
    }

    async togglePrediction () {
        if (this.isPredicting) {
            this.stopPrediction();
        } else {
            await this.startPrediction();
        }
    }

    _schedulePrediction () {
        this._predictFrame = requestAnimationFrame(async () => {
            if (!this.isPredicting) {
                return;
            }

            try {
                const activation = this.tf.tidy(() =>
                    this.mobilenetModel.infer(this.tf.browser.fromPixels(this.analysisVideo), true)
                );
                const result = await this.classifier.predictClass(activation);
                activation.dispose();

                this.setPredictionForType(TRAINING_INPUT_TYPES.IMAGE, result);
                this.emitChange();
            } catch (error) {
                this.error = error.message;
                this.status = 'Forudsigelsen mislykkedes.';
                this.stopPrediction();
                return;
            }

            this._schedulePrediction();
        });
    }

    clearTraining () {
        this.stopAddingExamples();
        if (this.classifier) {
            this.classifier.clearAllClasses();
        }
        this.trainingCounts = this.labels.reduce((counts, label) => {
            counts[label] = 0;
            return counts;
        }, {});
        this.examplesByLabel = this.labels.reduce((result, label) => {
            result[label] = [];
            return result;
        }, {});
        this.resetPredictions();
        this.isPredicting = false;
        if (this._predictFrame) {
            cancelAnimationFrame(this._predictFrame);
            this._predictFrame = null;
        }
        this.stopAudioPredictionInput();
        this.status = 'Datatræningen er ryddet fra browserens hukommelse.';
        this.emitChange();
        if (!this.previewVisible) {
            this.stopVideo();
        }
    }

    closePreview () {
        this.previewVisible = false;
        if (this.isPredicting) {
            this.emitChange();
        } else {
            this.stopVideo();
        }
    }

    stopVideo () {
        if (!this.stream) {
            return;
        }

        const shouldStopTrack = requestDisableVideo();
        if (shouldStopTrack) {
            this.stream.getTracks().forEach(track => track.stop());
        }

        this.stream = null;
        this.analysisVideo.srcObject = null;
        this.analysisVideo.onloadedmetadata = null;
        this.emitChange();
    }

    startRecordingAudioExample (label) {
        if (this.audioRecorder || this.isRecordingAudio) {
            return;
        }
        this.stopAudioPredictionInput();
        this.audioRecorder = new AudioRecorder();
        this.isRecordingAudio = true;
        this.status = `Optager lyd til "${label}"...`;
        this.emitChange();
        this.audioRecorder.startListening(
            () => this.audioRecorder && this.audioRecorder.startRecording(),
            level => {
                this.audioLevel = level;
                this.emitChange();
            },
            error => {
                this.audioRecorder = null;
                this.isRecordingAudio = false;
                this._setErrorState(error, 'Mikrofonen kunne ikke startes.');
            }
        );
        this._captureLabel = label;
    }

    stopRecordingAudioExample () {
        const recorder = this.audioRecorder;
        const label = this._captureLabel;
        this.audioRecorder = null;
        this._captureLabel = null;
        this.isRecordingAudio = false;
        this.audioLevel = 0;
        if (!recorder || !label) {
            this.emitChange();
            return;
        }

        const {samples, sampleRate} = recorder.stop();
        recorder.dispose();
        if (samples && samples.length) {
            const example = this.createAudioExample(
                samples,
                sampleRate,
                `${(samples.length / sampleRate).toFixed(1)} sek.`
            );
            this.addStoredExample(label, example);
            this.trainingCounts[label] = (this.trainingCounts[label] || 0) + 1;
            this.status = `Tilfoejede et lydeksempel til "${label}".`;
        } else {
            this.status = 'Optagelsen var tom. Proev igen.';
        }
        this.emitChange();
    }

    async ensureAudioPredictionInput () {
        if (this.audioStream) return;
        this.audioContext = new SharedAudioContext();
        this.audioStream = await navigator.mediaDevices.getUserMedia({audio: true});
        this.audioSourceNode = this.audioContext.createMediaStreamSource(this.audioStream);
        this.audioProcessorNode = this.audioContext.createScriptProcessor(4096, 1, 1);
        this.audioProcessorNode.onaudioprocess = event => {
            if (!this.isPredicting) return;
            const input = event.inputBuffer.getChannelData(0);
            for (let i = 0; i < input.length; i++) {
                this.audioPredictionBuffer.push(input[i]);
            }
            const maxSamples = this.audioContext.sampleRate;
            if (this.audioPredictionBuffer.length > maxSamples) {
                this.audioPredictionBuffer = this.audioPredictionBuffer.slice(
                    this.audioPredictionBuffer.length - maxSamples
                );
            }
            if (this.audioPredictionBuffer.length >= maxSamples / 2) {
                const features = samplesToAudioFeatures(new Float32Array(this.audioPredictionBuffer));
                const result = classifyAudioFeatures(this.audioClassifier, features);
                this.setPredictionForType(TRAINING_INPUT_TYPES.AUDIO, result);
                this.emitChange();
            }
        };
        this.audioSourceNode.connect(this.audioProcessorNode);
        this.audioProcessorNode.connect(this.audioContext.destination);
    }

    stopAudioPredictionInput () {
        if (this.audioProcessorNode) {
            this.audioProcessorNode.onaudioprocess = null;
            this.audioProcessorNode.disconnect();
            this.audioProcessorNode = null;
        }
        if (this.audioSourceNode) {
            this.audioSourceNode.disconnect();
            this.audioSourceNode = null;
        }
        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
        }
        this.audioPredictionBuffer = [];
    }

    teardown () {
        this.stopAddingExamples();
        this.stopPrediction();
        this.clearTraining();
        this.stopVideo();
        this.stopAudioPredictionInput();
        this.runtime.removeListener('VISION_TRAINER_CLEAR_REQUEST', this._handleClearRequest);
        window.removeEventListener('beforeunload', this._handleBeforeUnload);
    }
}

const getVisionClassifierSession = vm => {
    if (!vm.runtime.__visionClassifierSession) {
        vm.runtime.__visionClassifierSession = new VisionClassifierSession(vm.runtime);
    }

    return vm.runtime.__visionClassifierSession;
};

export {
    VisionClassifierSession,
    getVisionClassifierSession
};
